import type { UserProfile } from "../../types/user";
import { TIER_LIMITS, coachLimit, isPaidTier } from "../../types/user";

type PlanAction =
  | { readonly label: "Upgrade"; readonly to: "/pricing" }
  /** /profile, not /pricing: the Stripe billing portal button is on the Profile page; /pricing only sells Pro. */
  | { readonly label: "Manage"; readonly to: "/profile" };

/** What the account button and its menu say about the plan (#250): this month's coached reviews against the plan's
 *  limit, and where the plan's action goes. */
export interface PlanUsage {
  /** "Free", on the button. */
  readonly label: string;
  /** "Free plan", in the menu. */
  readonly title: string;
  /** "0/5", on the button. */
  readonly count: string;
  /** "0 of 5 coached reviews this month", in the menu. */
  readonly line: string;
  /** No coached reviews left this month, on any plan. */
  readonly atLimit: boolean;
  readonly action: PlanAction;
}

export function planUsage(profile: Pick<UserProfile, "tier" | "coachCallsThisMonth" | "unmetered">): PlanUsage {
  const tier = TIER_LIMITS[profile.tier] ?? TIER_LIMITS.free;
  const used = profile.coachCallsThisMonth ?? 0;
  const limit = coachLimit(profile);
  // an unmetered account (set by hand) has no ceiling: its count, no limit
  const unmetered = limit === Infinity;
  return {
    label: tier.label,
    title: `${tier.label} plan`,
    count: unmetered ? `${used}` : `${used}/${limit}`,
    line: unmetered ? `${used} coached reviews this month, no limit` : `${used} of ${limit} coached reviews this month`,
    atLimit: !unmetered && used >= limit,
    action: isPaidTier(profile.tier) ? { label: "Manage", to: "/profile" } : { label: "Upgrade", to: "/pricing" },
  };
}
